import { Disclosure } from '@headlessui/react';
import {
  ChevronDown,
  Mail,
  Printer,
  RefreshCw,
  Send,
  Settings,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useRefreshFaxStatusMutation } from '@/services/api/faxService';
import type { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';

import { ConfigureStrategyModal } from '../request/cards/followups/ConfigureStrategyModal';
import type { TransmissionHistoryEntry } from '../request/cards/followups/hooks/useFollowUpTransmission';
import { useFollowUpTransmission } from '../request/cards/followups/hooks/useFollowUpTransmission';
import { SendFollowupModal } from '../request/cards/followups/SendFollowupModal';
import { FollowUpDots } from './FollowUpDots';

interface CompactFollowUpsProps {
  matterId: string;
  providerId: string;
  request: TreatmentRecordRequestSchema;
  frontConversationUrl?: string | null;
}

const formatDate = (date: Date | null) => {
  if (!date) return null;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const getChannelLabel = (channel: string | null | undefined) => {
  if (!channel) return 'Unknown';
  const normalized = channel.toLowerCase();
  if (normalized.includes('fax')) return 'Fax';
  if (normalized.includes('email')) return 'Email';
  if (normalized.includes('mail') || normalized.includes('letter'))
    return 'Mail';
  return channel;
};

/**
 * Format interval days with correct grammar (1 day vs X days)
 */
const formatIntervalDays = (days: number | undefined): string => {
  const intervalDays = days || 3;
  return intervalDays === 1 ? '1 day' : `${intervalDays} days`;
};

/**
 * Get icon for transmission channel
 */
const getChannelIcon = (channel: string | null | undefined) => {
  const normalized = channel?.toLowerCase() || '';
  if (normalized.includes('fax')) return <Printer className="h-3 w-3" />;
  if (normalized.includes('email')) return <Mail className="h-3 w-3" />;
  return <Send className="h-3 w-3" />;
};

/**
 * Check if transmission was triggered automatically
 */
const isAutomatic = (triggeredBy: string | undefined): boolean => {
  const normalized = triggeredBy?.toLowerCase() || '';
  return (
    normalized === 'auto' ||
    normalized === 'automatic' ||
    normalized === 'scheduled'
  );
};

/**
 * Get description text for a transmission entry
 */
const getEntryDescription = (entry: TransmissionHistoryEntry): string => {
  const auto = isAutomatic(entry.triggered_by);
  if (entry.isInitial) {
    return auto ? 'Initial submission' : 'Manual initial submission';
  }
  return auto ? 'Automatic follow-up' : 'Manual follow-up';
};

/**
 * Get status badge styling
 */
const getStatusStyle = (status: string | undefined) => {
  const normalized = status?.toLowerCase() || '';
  if (['delivered', 'success', 'completed'].includes(normalized)) {
    return 'bg-green-100 text-green-700';
  }
  if (['sent', 'pending', 'queued'].includes(normalized)) {
    return 'bg-yellow-100 text-yellow-700';
  }
  if (['failed', 'error', 'rejected'].includes(normalized)) {
    return 'bg-red-100 text-red-700';
  }
  return 'bg-gray-100 text-gray-600';
};

/**
 * Get display label for status
 */
const getStatusLabel = (status: string | undefined): string => {
  const normalized = status?.toLowerCase() || '';
  if (['delivered', 'success', 'completed'].includes(normalized))
    return 'delivered';
  if (['sent', 'pending', 'queued'].includes(normalized)) return 'sent';
  if (['failed', 'error', 'rejected'].includes(normalized)) return 'failed';
  return status || 'sent';
};

/**
 * Compact disclosure component for follow-up transmissions.
 * Matches the pattern of CompactAlternates with expand/collapse.
 *
 * Header shows: "Follow-ups [dots] • Next: Jan 18"
 * Panel shows: strategy info, mini-timeline, history items, action buttons
 */
export const CompactFollowUps = ({
  matterId,
  providerId,
  request,
  frontConversationUrl,
}: CompactFollowUpsProps) => {
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const [refreshFaxStatus] = useRefreshFaxStatusMutation();

  const {
    history,
    strategy,
    totalSent,
    maxAttempts,
    isAutoEnabled,
    isComplete,
    nextScheduledDate,
    isLoading,
    refetchHistory,
  } = useFollowUpTransmission({
    matterId,
    providerId,
    request,
  });

  const handleRefreshStatus = async (entryId: string) => {
    setRefreshingId(entryId);
    try {
      await refreshFaxStatus(entryId).unwrap();
      await refetchHistory();
      toast.success('Status refreshed');
    } catch {
      toast.error('Failed to refresh status');
    } finally {
      setRefreshingId(null);
    }
  };

  // Check if request is in a submitted status (API query runs for these)
  const SUBMITTED_STATUSES = [
    'request_submitted',
    'request_received',
    'awaiting_invoice',
    'pending_payment',
    'records_pending',
    'records_delivered',
    'records_ingested',
    'rejected',
    'cancelled',
  ];
  const hasSubmittedRequest =
    !!request.status && SUBMITTED_STATUSES.includes(request.status);

  // Don't show for non-submitted requests
  if (!hasSubmittedRequest) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="border-t border-zinc-100 pt-2 mt-2">
        <div className="flex items-center gap-1.5 text-xs py-1 text-zinc-400">
          <Send className="h-3 w-3 animate-pulse" />
          <span>Loading follow-ups...</span>
        </div>
      </div>
    );
  }

  // Show empty state with send button when no history yet
  const hasNoHistory = history.length === 0;

  /**
   * Get minimal empty state message
   */
  const getEmptyStateMessage = (): string | null => {
    if (isAutoEnabled && nextScheduledDate) {
      return `First follow-up: ${formatDate(nextScheduledDate)}`;
    }
    if (!isAutoEnabled) {
      return 'Use "Send Now", or configure automatic follow-ups.';
    }
    return null;
  };

  const getStatusText = () => {
    if (hasNoHistory) {
      if (isAutoEnabled) return 'Auto-enabled';
      return 'No follow-ups sent';
    }
    if (isComplete) return 'Complete';
    if (isAutoEnabled) return `Next: ${formatDate(nextScheduledDate)}`;
    // When auto-disabled with history, show sent count in header
    return `${totalSent} of ${maxAttempts} sent`;
  };
  const statusText = getStatusText();

  return (
    <>
      <Disclosure>
        {({ open }) => (
          <div className="border-t border-zinc-100 pt-2 mt-2">
            <Disclosure.Button className="flex w-full items-center justify-between text-xs py-1">
              <div className="flex items-center gap-1.5">
                <Send className="h-3 w-3 text-amber-600" />
                <span className="font-medium text-amber-700">
                  Follow-Up with Provider
                </span>
                {!hasNoHistory && (
                  <>
                    <FollowUpDots history={history} total={maxAttempts} />
                    <span className="text-zinc-400">•</span>
                  </>
                )}
                <span className="text-zinc-600">{statusText}</span>
              </div>
              <ChevronDown
                className={`h-3 w-3 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </Disclosure.Button>
            <Disclosure.Panel className="mt-2 space-y-2.5">
              {/* Status badge showing auto/manual mode and progress */}
              {strategy && (
                <div className="px-1">
                  <div
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs',
                      isAutoEnabled
                        ? 'bg-green-50 text-green-700'
                        : 'bg-gray-100 text-gray-600',
                    )}
                  >
                    <span
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        isAutoEnabled ? 'bg-green-500' : 'bg-gray-400',
                      )}
                    />
                    <span className="font-medium">
                      {isAutoEnabled ? 'Automatic' : 'Manual only'}
                    </span>
                    <span className="text-gray-400">•</span>
                    {getChannelIcon(strategy.channel)}
                    <span>
                      {getChannelLabel(strategy.channel)} every{' '}
                      {formatIntervalDays(strategy.interval_days)}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span>
                      {totalSent} of {maxAttempts} sent
                    </span>
                  </div>
                </div>
              )}

              {/* Empty state messaging */}
              {hasNoHistory && getEmptyStateMessage() && (
                <div className="text-xs text-zinc-500 px-1 py-2">
                  {getEmptyStateMessage()}
                </div>
              )}

              {/* History items - streamlined rows */}
              {!hasNoHistory && (
                <div className="space-y-1">
                  {history.slice(0, 3).map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-2 bg-zinc-50 rounded text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-400">
                          {getChannelIcon(entry.channelIcon)}
                        </span>
                        <div>
                          <span className="font-medium text-zinc-900">
                            {formatDate(
                              new Date(
                                entry.transmitted_at || entry.created_at,
                              ),
                            )}
                          </span>
                          <span className="text-zinc-500 ml-1.5">
                            {getEntryDescription(entry)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {frontConversationUrl && (
                          <a
                            href={frontConversationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            Front →
                          </a>
                        )}
                        <span
                          className={cn(
                            'px-1.5 py-0.5 rounded text-[10px] font-medium',
                            getStatusStyle(entry.status),
                          )}
                        >
                          {getStatusLabel(entry.status)}
                        </span>
                        {entry.channelIcon === 'fax' &&
                          entry.fax_transmission_id && (
                            <button
                              type="button"
                              onClick={() =>
                                handleRefreshStatus(entry.fax_transmission_id!)
                              }
                              disabled={
                                refreshingId === entry.fax_transmission_id
                              }
                              className="p-0.5 text-zinc-400 hover:text-zinc-600 disabled:opacity-50"
                              title="Refresh fax status"
                            >
                              <RefreshCw
                                className={cn(
                                  'h-3 w-3',
                                  refreshingId === entry.fax_transmission_id &&
                                    'animate-spin',
                                )}
                              />
                            </button>
                          )}
                      </div>
                    </div>
                  ))}
                  {history.length > 3 && (
                    <div className="text-xs text-zinc-500 text-center py-1">
                      +{history.length - 3} more
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsSendModalOpen(true)}
                  className="h-7 text-xs"
                >
                  <Send className="h-3 w-3 mr-1" />
                  Send Now
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsConfigModalOpen(true)}
                  className="h-7 text-xs"
                >
                  <Settings className="h-3 w-3 mr-1" />
                  Configure
                </Button>
              </div>
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>

      {/* Modals */}
      <SendFollowupModal
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        matterId={matterId}
        providerId={providerId}
        request={request}
      />

      <ConfigureStrategyModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        matterId={matterId}
        providerId={providerId}
        requestId={request.id}
        currentStrategy={strategy}
        totalSent={totalSent}
        nextScheduledDate={nextScheduledDate}
      />
    </>
  );
};
